import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Activity, TrendingUp, BarChart3, Zap, Shield, Bell, Target, 
  ChevronRight, Play, Check, ArrowRight, LineChart, Brain,
  Wallet, Clock, Users, Star
} from 'lucide-react';

const FEATURES = [
  { 
    icon: Target, 
    title: 'Smart Stock Scanner', 
    desc: 'Auto-detect entry zones, fair value, and profit targets using technical analysis',
    color: 'from-blue-500 to-cyan-500'
  },
  { 
    icon: Brain, 
    title: 'AI Daily Briefings', 
    desc: 'GPT-powered market analysis delivered every morning before market open',
    color: 'from-purple-500 to-pink-500'
  },
  { 
    icon: BarChart3, 
    title: 'Trade Score System', 
    desc: 'A+ setups ranked by technical confluence - only trade the best',
    color: 'from-green-500 to-emerald-500'
  },
  { 
    icon: Zap, 
    title: 'Whale Alerts', 
    desc: 'Track institutional flow, dark pool activity, and insider trades',
    color: 'from-yellow-500 to-orange-500'
  },
  { 
    icon: LineChart, 
    title: 'Backtesting Engine', 
    desc: 'Test strategies against historical data before risking real capital',
    color: 'from-red-500 to-rose-500'
  },
  { 
    icon: Bell, 
    title: 'Liquidity Sweep Alerts', 
    desc: 'Get notified when institutional players sweep liquidity levels',
    color: 'from-indigo-500 to-violet-500'
  },
];

const STATS = [
  { value: '50+', label: 'Technical Indicators' },
  { value: '24/7', label: 'Market Monitoring' },
  { value: 'A+', label: 'Setup Detection' },
  { value: 'Free', label: 'Smart Analysis' },
];

export default function LandingPage({ onGetStarted, onTryDemo }) {
  const [hoveredFeature, setHoveredFeature] = useState(null);

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold">TradeRelax</span>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                className="text-slate-300 hover:text-white hidden sm:flex"
                onClick={onTryDemo}
              >
                <Play className="w-4 h-4 mr-2" />
                Try Demo
              </Button>
              <Button 
                onClick={onGetStarted}
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500"
              >
                Get Started
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-600/10 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-40 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-60 right-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl pointer-events-none" />
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-full border border-slate-700/50 mb-8">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-sm text-slate-300">Wait for A+ setups only</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Trade Smarter with
            <span className="block bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
              AI-Powered Analysis
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-10">
            Professional-grade trading dashboard with real-time scanners, 
            AI briefings, and whale alerts. Only trade when the setup is perfect.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Button 
              size="lg"
              onClick={onGetStarted}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-lg px-8 py-6 h-auto"
            >
              Start Trading Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={onTryDemo}
              className="w-full sm:w-auto border-slate-700 hover:bg-slate-800 text-lg px-8 py-6 h-auto"
            >
              <Play className="w-5 h-5 mr-2" />
              Try Demo
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl mx-auto">
            {STATS.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-white">{stat.value}</div>
                <div className="text-sm text-slate-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Everything You Need to Trade</h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              Built by traders, for traders. Every feature designed to help you find and execute A+ setups.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, i) => (
              <div
                key={i}
                className="group p-6 bg-slate-800/50 rounded-2xl border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300 hover:-translate-y-1"
                onMouseEnter={() => setHoveredFeature(i)}
                onMouseLeave={() => setHoveredFeature(null)}
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">How It Works</h2>
            <p className="text-slate-400">Three simple steps to better trading</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Scan Markets', desc: 'Our AI scans the market 24/7 looking for high-probability setups', icon: Target },
              { step: '02', title: 'Get Alerts', desc: 'Receive instant notifications when A+ setups are detected', icon: Bell },
              { step: '03', title: 'Execute', desc: 'Paper trade automatically or execute manually with confidence', icon: Zap },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="relative inline-block mb-6">
                  <div className="w-16 h-16 bg-slate-800 rounded-2xl flex items-center justify-center border border-slate-700">
                    <item.icon className="w-8 h-8 text-blue-400" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-sm font-bold">
                    {item.step}
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-slate-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-8 sm:p-12 border border-slate-700/50 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />
            
            <div className="relative z-10 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Trade Smarter?</h2>
              <p className="text-slate-400 mb-8 max-w-xl mx-auto">
                Join traders who only take A+ setups. Track smart money, follow whale alerts, and profit.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button 
                  size="lg"
                  onClick={onGetStarted}
                  className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 px-8"
                >
                  Create Free Account
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={onTryDemo}
                  className="w-full sm:w-auto border-slate-600 hover:bg-slate-800 px-8"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Try Demo First
                </Button>
              </div>

              <div className="flex items-center justify-center gap-6 mt-8 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" />
                  No credit card
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" />
                  AI-powered signals
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" />
                  Cancel anytime
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-md flex items-center justify-center">
              <Activity className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold">TradeRelax</span>
          </div>
          <p className="text-sm text-slate-500">
            © 2025 TradeRelax. Trade responsibly.
          </p>
        </div>
      </footer>
    </div>
  );
}
